import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { editorsConfigApi, localAuthApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Globe, Key, Eye, EyeOff } from 'lucide-react';

interface ConfigItem {
  key: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  placeholder: string;
}

const configItems: ConfigItem[] = [
  {
    key: 'vscode_url',
    label: 'VS Code Web URL',
    description:
      'The URL of your VS Code Web server. Use https://vsc.your-domain.com for production or http://localhost:3004 for development.',
    icon: <Globe className="h-4 w-4" />,
    placeholder: 'https://vsc.private-ai.stunited.vn',
  },
  {
    key: 'vscode_connection_token',
    label: 'Connection Token',
    description:
      'The connection token used to authenticate with VS Code Web. Must match the token set when starting the openvscode-server.',
    icon: <Key className="h-4 w-4" />,
    placeholder: 'your-secret-token',
  },
];

export function EditorsConfigSettings() {
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [successKey, setSuccessKey] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const queryClient = useQueryClient();

  const { data: currentUser } = useQuery({
    queryKey: ['localUser'],
    queryFn: localAuthApi.getCurrentUser,
  });

  const { data: configEntries, isLoading } = useQuery({
    queryKey: ['editorsConfig'],
    queryFn: editorsConfigApi.list,
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      editorsConfigApi.update(key, value),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['editorsConfig'] });
      setSuccessKey(variables.key);
      setEditedValues((prev) => {
        const next = { ...prev };
        delete next[variables.key];
        return next;
      });
      setError(null);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessKey(null), 3000);
    },
    onError: (error: Error) => {
      setError(error.message);
      setSuccessKey(null);
    },
  });

  // Check admin permission
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">
          You do not have permission to access this page.
        </p>
      </div>
    );
  }

  const getValue = (key: string): string => {
    if (key in editedValues) {
      return editedValues[key];
    }
    const entry = configEntries?.find((e) => e.key === key);
    return entry?.value ?? '';
  };

  const handleChange = (key: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
    setSuccessKey(null);
    setError(null);
  };

  const handleSave = (key: string) => {
    const value = getValue(key);
    updateMutation.mutate({ key, value });
  };

  const hasChanges = (key: string): boolean => {
    return key in editedValues;
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Loading configuration...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">
          Editor Configuration
        </h2>
        <p className="text-muted-foreground">
          Configure settings for browser-based code editors like VS Code Web.
        </p>
      </div>

      {error && (
        <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {configItems.map((item) => (
          <div key={item.key} className="space-y-2">
            <div className="flex items-center gap-2">
              {item.icon}
              <Label htmlFor={item.key} className="text-base font-medium">
                {item.label}
              </Label>
            </div>
            <p className="text-sm text-muted-foreground">{item.description}</p>
            <div className="flex items-center gap-2">
              <Input
                id={item.key}
                type={
                  item.key === 'vscode_connection_token' && !showToken
                    ? 'password'
                    : 'text'
                }
                value={getValue(item.key)}
                onChange={(e) => handleChange(item.key, e.target.value)}
                placeholder={item.placeholder}
                className="flex-1"
              />
              {item.key === 'vscode_connection_token' && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowToken(!showToken)}
                  className="h-10 w-10"
                >
                  {showToken ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              )}
              <Button
                onClick={() => handleSave(item.key)}
                disabled={!hasChanges(item.key) || updateMutation.isPending}
                variant={successKey === item.key ? 'default' : 'outline'}
              >
                {updateMutation.isPending &&
                updateMutation.variables?.key === item.key ? (
                  'Saving...'
                ) : successKey === item.key ? (
                  'Saved!'
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 p-4 bg-muted/50 rounded-lg">
        <h3 className="font-medium mb-2">Setup Instructions</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
          <li>
            Start openvscode-server with your connection token:
            <pre className="mt-1 p-2 bg-muted rounded text-xs overflow-x-auto">
              pm2 start "&lt;path-to-openvscode-server&gt;/bin/openvscode-server --port 3004
              --connection-token YOUR_TOKEN --default-folder
              /var/tmp/vibe-kanban/worktrees --server-base-path /vscode" --name
              openvscode-server
            </pre>
            <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
              <strong>Note:</strong> For dev mode, use{' '}
              <code className="px-1 bg-muted rounded">vibe-kanban-dev</code>{' '}
              instead of{' '}
              <code className="px-1 bg-muted rounded">vibe-kanban</code> as the
              default-folder.
            </p>
          </li>
          <li>Set the same token in the "Connection Token" field above</li>
          <li>
            Set the VS Code Web URL (e.g., https://private-ai.stunited.vn/vscode
            for prod or http://localhost:3002/vscode for dev)
          </li>
        </ol>
      </div>
    </div>
  );
}